import { Calendar, Users } from "lucide-react";
import { memo, useCallback, useState } from "react";
import { type Event, getEventColor } from "@/lib/api";
import { cn } from "@trotroways/utils";

interface EventPinProps {
  event: Event;
  isSelected?: boolean;
  isCompact?: boolean;
  onClick?: () => void;
}

function formatEventDate(dateStr: string): { day: string; month: string } {
  const date = new Date(dateStr);
  return {
    day: date.getDate().toString(),
    month: date.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
  };
}

export const EventPin = memo(function EventPin({
  event,
  isSelected = false,
  isCompact = false,
  onClick,
}: EventPinProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const eventColor = getEventColor(event.event_type);
  const dateInfo = formatEventDate(event.start_time);
  const hasImage = event.cover_image_url && !imageError;
  const additionalImages =
    event.images?.filter((img) => img !== event.cover_image_url) || [];

  const handleImageLoad = useCallback(() => setImageLoaded(true), []);
  const handleImageError = useCallback(() => setImageError(true), []);

  if (isCompact) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "event-pin-compact group relative flex items-center justify-center",
          "transition-all duration-200 ease-out",
          isSelected && "scale-110",
        )}
        style={{ touchAction: "manipulation" }}
      >
        <div
          className={cn(
            "relative w-12 h-12 rounded-full overflow-hidden",
            "border-3 border-white shadow-lg",
            "transition-transform duration-200",
            "group-hover:scale-105",
          )}
          style={{
            boxShadow: isSelected
              ? `0 4px 12px rgba(0,0,0,0.25), 0 0 0 3px ${eventColor}40`
              : "0 2px 8px rgba(0,0,0,0.2)",
          }}
        >
          {hasImage ? (
            <img
              src={event.cover_image_url ?? ""}
              alt={event.title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center text-white font-bold text-lg"
              style={{ backgroundColor: eventColor }}
            >
              {event.title.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div
          className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white shadow-sm"
          style={{ backgroundColor: eventColor }}
        >
          {dateInfo.day}
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "event-pin group relative",
        "transition-all duration-200 ease-out",
        isSelected && "scale-105 z-10",
      )}
      style={{ touchAction: "manipulation" }}
    >
      <div
        className={cn(
          "relative w-44 bg-white rounded-xl overflow-hidden",
          "shadow-lg transition-shadow duration-200",
          "group-hover:shadow-xl",
          isSelected && "ring-2 ring-offset-2",
        )}
        style={{
          boxShadow: isSelected
            ? `0 8px 24px rgba(0,0,0,0.2), 0 0 0 2px ${eventColor}`
            : "0 4px 12px rgba(0,0,0,0.15)",
        }}
      >
        <div className="relative aspect-4/3 overflow-hidden">
          {hasImage ? (
            <>
              <img
                src={event.cover_image_url ?? ""}
                alt={event.title}
                className={cn(
                  "w-full h-full object-cover transition-opacity duration-300",
                  imageLoaded ? "opacity-100" : "opacity-0",
                )}
                loading="lazy"
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
              {!imageLoaded && (
                <div
                  className="absolute inset-0 animate-pulse"
                  style={{
                    backgroundColor: `${eventColor}20`,
                  }}
                />
              )}
            </>
          ) : (
            <div
              className="w-full h-full flex items-center justify-center"
              style={{ backgroundColor: `${eventColor}15` }}
            >
              <Calendar className="w-8 h-8" style={{ color: eventColor }} />
            </div>
          )}

          {additionalImages.length > 0 && (
            <div className="absolute bottom-2 right-2 flex -space-x-2">
              {additionalImages.slice(0, 2).map((img, idx) => (
                <div
                  key={idx}
                  className="w-6 h-6 rounded border-2 border-white shadow-sm overflow-hidden"
                  style={{
                    transform: `rotate(${idx * 5 - 2}deg)`,
                  }}
                >
                  <img
                    src={img}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              ))}
              {additionalImages.length > 2 && (
                <div className="w-6 h-6 rounded border-2 border-white bg-black/60 flex items-center justify-center">
                  <span className="text-[10px] text-white font-medium">
                    +{additionalImages.length - 2}
                  </span>
                </div>
              )}
            </div>
          )}

          <div
            className="absolute top-2 left-2 px-2 py-1 rounded-md text-white text-center shadow-sm"
            style={{ backgroundColor: eventColor }}
          >
            <div className="text-xs font-bold leading-none">{dateInfo.day}</div>
            <div className="text-[9px] font-medium leading-tight opacity-90">
              {dateInfo.month}
            </div>
          </div>
        </div>

        <div className="p-2.5">
          <div
            className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide text-white mb-1.5"
            style={{ backgroundColor: eventColor }}
          >
            {event.title.length > 20
              ? `${event.title.slice(0, 20)}...`
              : event.title}
          </div>

          {event.venue_name && (
            <p className="text-[10px] text-gray-500 truncate mb-1.5">
              {event.venue_name}
            </p>
          )}

          {(event.attendee_count ?? 0) > 0 && (
            <div className="flex items-center gap-1.5">
              <div className="flex -space-x-1.5">
                {Array.from({
                  length: Math.min(event.attendee_count || 0, 4),
                }).map((_, idx) => (
                  <div
                    key={idx}
                    className="w-5 h-5 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center"
                    style={{
                      backgroundColor: `hsl(${(idx * 60 + 200) % 360}, 70%, 85%)`,
                      zIndex: 4 - idx,
                    }}
                  >
                    <Users className="w-2.5 h-2.5 text-gray-600" />
                  </div>
                ))}
              </div>
              <span className="text-[10px] text-gray-500">
                {event.attendee_count} going
              </span>
            </div>
          )}
        </div>
      </div>

      <div
        className="absolute left-1/2 -bottom-2 w-3 h-3 bg-white rotate-45 -translate-x-1/2 shadow-sm"
        style={{
          boxShadow: "2px 2px 4px rgba(0,0,0,0.1)",
        }}
      />
    </button>
  );
});

export function createEventPinElement(
  event: Event,
  isSelected: boolean,
  isCompact: boolean,
  onClick: () => void,
): HTMLDivElement {
  const container = document.createElement("div");
  container.className = "event-pin-container";
  container.style.cssText = "cursor: pointer; touch-action: manipulation;";

  const eventColor = getEventColor(event.event_type);
  const dateInfo = formatEventDate(event.start_time);
  const hasImage = !!event.cover_image_url;

  if (isCompact) {
    container.innerHTML = `
      <div class="event-pin-compact ${isSelected ? "selected" : ""}" style="
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform 0.2s ease-out;
        ${isSelected ? "transform: scale(1.1);" : ""}
      ">
        <div style="
          position: relative;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          overflow: hidden;
          border: 3px solid white;
          box-shadow: ${isSelected ? `0 4px 12px rgba(0,0,0,0.25), 0 0 0 3px ${eventColor}40` : "0 2px 8px rgba(0,0,0,0.2)"};
          transition: transform 0.2s;
        ">
          ${hasImage
        ? `<img src="${event.cover_image_url}" alt="${event.title}" style="width: 100%; height: 100%; object-fit: cover;" loading="lazy" />`
        : `<div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background-color: ${eventColor}; color: white; font-weight: bold; font-size: 18px;">${event.title.charAt(0).toUpperCase()}</div>`
      }
        </div>
        <div style="
          position: absolute;
          top: -4px;
          right: -4px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: bold;
          color: white;
          background-color: ${eventColor};
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        ">${dateInfo.day}</div>
      </div>
    `;
  } else {
    const additionalImages =
      event.images?.filter((img) => img !== event.cover_image_url) || [];
    const attendeeCount = event.attendee_count || 0;

    container.innerHTML = `
      <div class="event-pin ${isSelected ? "selected" : ""}" style="
        position: relative;
        transition: transform 0.2s ease-out;
        ${isSelected ? "transform: scale(1.05); z-index: 10;" : ""}
      ">
        <div style="
          position: relative;
          width: 176px;
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: ${isSelected ? `0 8px 24px rgba(0,0,0,0.2), 0 0 0 2px ${eventColor}` : "0 4px 12px rgba(0,0,0,0.15)"};
          transition: box-shadow 0.2s;
        ">
          <div style="position: relative; aspect-ratio: 4/3; overflow: hidden;">
            ${hasImage
        ? `<img src="${event.cover_image_url}" alt="${event.title}" style="width: 100%; height: 100%; object-fit: cover;" loading="lazy" />`
        : `<div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background-color: ${eventColor}15;">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="${eventColor}" stroke-width="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                  </div>`
      }
            ${additionalImages.length > 0
        ? `
              <div style="position: absolute; bottom: 8px; right: 8px; display: flex;">
                ${additionalImages
          .slice(0, 2)
          .map(
            (img, idx) => `
                  <div style="width: 24px; height: 24px; border-radius: 4px; border: 2px solid white; box-shadow: 0 1px 2px rgba(0,0,0,0.1); overflow: hidden; margin-left: ${idx > 0 ? "-8px" : "0"}; transform: rotate(${idx * 5 - 2}deg);">
                    <img src="${img}" alt="" style="width: 100%; height: 100%; object-fit: cover;" loading="lazy" />
                  </div>
                `,
          )
          .join("")}
                ${additionalImages.length > 2
          ? `
                  <div style="width: 24px; height: 24px; border-radius: 4px; border: 2px solid white; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; margin-left: -8px;">
                    <span style="font-size: 10px; color: white; font-weight: 500;">+${additionalImages.length - 2}</span>
                  </div>
                `
          : ""
        }
              </div>
            `
        : ""
      }
            <div style="
              position: absolute;
              top: 8px;
              left: 8px;
              padding: 4px 8px;
              border-radius: 6px;
              background-color: ${eventColor};
              color: white;
              text-align: center;
              box-shadow: 0 1px 3px rgba(0,0,0,0.2);
            ">
              <div style="font-size: 12px; font-weight: bold; line-height: 1;">${dateInfo.day}</div>
              <div style="font-size: 9px; font-weight: 500; line-height: 1.2; opacity: 0.9;">${dateInfo.month}</div>
            </div>
          </div>
          <div style="padding: 10px;">
            <div style="
              display: inline-block;
              padding: 2px 8px;
              border-radius: 4px;
              font-size: 10px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              color: white;
              background-color: ${eventColor};
              margin-bottom: 6px;
            ">${event.title.length > 20 ? `${event.title.slice(0, 20)}...` : event.title}</div>
            ${event.venue_name ? `<p style="font-size: 10px; color: #6b7280; margin: 0 0 6px 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${event.venue_name}</p>` : ""}
            ${attendeeCount > 0
        ? `
              <div style="display: flex; align-items: center; gap: 6px;">
                <div style="display: flex;">
                  ${Array.from({ length: Math.min(attendeeCount, 4) })
          .map(
            (_, idx) => `
                    <div style="
                      width: 20px;
                      height: 20px;
                      border-radius: 50%;
                      border: 2px solid white;
                      background-color: hsl(${(idx * 60 + 200) % 360}, 70%, 85%);
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      margin-left: ${idx > 0 ? "-6px" : "0"};
                      z-index: ${4 - idx};
                    ">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#4b5563" stroke-width="2">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                      </svg>
                    </div>
                  `,
          )
          .join("")}
                </div>
                <span style="font-size: 10px; color: #6b7280;">${attendeeCount} going</span>
              </div>
            `
        : ""
      }
          </div>
        </div>
        <div style="
          position: absolute;
          left: 50%;
          bottom: -8px;
          width: 12px;
          height: 12px;
          background: white;
          transform: translateX(-50%) rotate(45deg);
          box-shadow: 2px 2px 4px rgba(0,0,0,0.1);
        "></div>
      </div>
    `;
  }

  container.addEventListener("click", onClick);

  return container;
}

export function updateEventPinElement(
  container: HTMLDivElement,
  event: Event,
  isSelected: boolean,
  isCompact: boolean,
): void {
  const eventColor = getEventColor(event.event_type);

  if (isCompact) {
    const pin = container.querySelector(".event-pin-compact") as HTMLElement;
    if (pin) {
      pin.style.transform = isSelected ? "scale(1.1)" : "";
      const circle = pin.querySelector("div > div:first-child") as HTMLElement;
      if (circle) {
        circle.style.boxShadow = isSelected
          ? `0 4px 12px rgba(0,0,0,0.25), 0 0 0 3px ${eventColor}40`
          : "0 2px 8px rgba(0,0,0,0.2)";
      }
    }
  } else {
    const pin = container.querySelector(".event-pin") as HTMLElement;
    if (pin) {
      pin.style.transform = isSelected ? "scale(1.05)" : "";
      pin.style.zIndex = isSelected ? "10" : "";
      const card = pin.querySelector("div > div:first-child") as HTMLElement;
      if (card) {
        card.style.boxShadow = isSelected
          ? `0 8px 24px rgba(0,0,0,0.2), 0 0 0 2px ${eventColor}`
          : "0 4px 12px rgba(0,0,0,0.15)";
      }
    }
  }
}
